import React from "react"

import {
  YotpoGetProductReviewsQueryParams,
  YotpoResponse,
  YotpoReview,
} from "./types"
import { useReviewClient } from "./useReviewClient"

export const useProductReviews = (
  productId: string,
  params?: YotpoGetProductReviewsQueryParams
) => {
  const { client } = useReviewClient()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | null | undefined>()
  const [data, setData] = React.useState<null | undefined | YotpoResponse>()
  const [reviews, setReviews] = React.useState<YotpoReview[]>([])

  // Derived values
  const avgRating = data?.bottomline.average_score ?? 0
  const numReviews = data?.bottomline.total_review ?? 0
  const goodReview = data?.reviews.find(({ score }) => score === 5)
  const hasMore = reviews.length < (data?.pagination.total ?? 0)
  const nextPage = (data?.pagination.page ?? 0) + 1

  // Because event listeners can't access updated state, need to use ref to access state on scroll
  const ref = React.useRef({
    hasMore,
    loading,
    nextPage,
    reviews,
  })
  ref.current.reviews = reviews
  ref.current.hasMore = reviews.length < (data?.pagination.total ?? 0)
  ref.current.loading = loading
  ref.current.nextPage = (data?.pagination.page ?? 0) + 1

  React.useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        const data = await client.getProductReviews(productId, params)
        setData(data)
        setReviews(data.reviews)
      } catch (e) {
        setError(e)
      } finally {
        setLoading(false)
      }
    })()
  }, [productId])

  const onScrollFetchMore = async () => {
    if (ref.current.hasMore && !ref.current.loading) {
      try {
        setLoading(true)

        const res = await client.getProductReviews(productId, {
          page: ref.current.nextPage,
        })

        setData(res)
        setReviews((prevState) => [...prevState, ...res.reviews])
      } catch (e) {
        setError(e)
      } finally {
        setLoading(false)
      }
    }
  }

  return {
    avgRating,
    data,
    error,
    goodReview,
    hasMore,
    loading,
    numReviews,
    onScrollFetchMore,
    reviews,
  }
}
